import _classCallCheck from "/codebuild/output/src3924087242/src/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src3924087242/src/node_modules/@babel/runtime/helpers/esm/createClass";
import { getUrlPrefix, getUrlVersion, handleAssetType, handleDeliveryType } from "../internal/url/cloudinaryURL.js";
import URLConfig from "../config/URLConfig.js";
import { getSDKAnalyticsSignature } from "../sdkAnalytics/getSDKAnalyticsSignature.js";
/**
 * This const contains all the valid combination of asset/delivery for URL shortening purposes
 * It's exported because it's used in a test, but it's not really shared enough to belong in a separate file
 */

export var SEO_TYPES = {
  "image/upload": "images",
  "image/private": "private_images",
  "image/authenticated": "authenticated_images",
  "raw/upload": "files",
  "video/upload": "videos"
};
/**
 * @description Cloudinary file without a transformation
 * @summary SDK
 * @memberOf SDK
 */

var CloudinaryFile = /*#__PURE__*/function () {
  function CloudinaryFile(publicID) {
    var cloudConfig = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var urlConfig = arguments.length > 2 ? arguments[2] : undefined;

    _classCallCheck(this, CloudinaryFile);

    this.setPublicID(publicID);
    this.setCloudConfig(cloudConfig);
    this.setURLConfig(urlConfig);
  }
  /**
   * @description Sets the URL Config for this asset
   * @param urlConfig
   * @return {this}
   */


  _createClass(CloudinaryFile, [{
    key: "setURLConfig",
    value: function setURLConfig(urlConfig) {
      this.urlConfig = new URLConfig(urlConfig);
      return this;
    }
    /**
     * @description Sets the Cloud Config for this asset
     * @param urlConfig
     * @return {this}
     */

  }, {
    key: "setCloudConfig",
    value: function setCloudConfig(cloudConfig) {
      this.cloudName = cloudConfig.cloudName;
      this.apiKey = cloudConfig.apiKey;
      this.apiSecret = cloudConfig.apiSecret;
      this.authToken = cloudConfig.authToken;
      return this;
    }
    /**
     * @description Sets the public ID of the asset.
     * @param {string} publicID The public ID of the asset.
     * @return {this}
     */

  }, {
    key: "setPublicID",
    value: function setPublicID(publicID) {
      // PublicID must be a string!
      this.publicID = publicID ? publicID.toString() : '';
      return this;
    }
    /**
     * @description Sets the delivery type of the asset.
     * @param {DELIVERY_TYPE | string} newType The type of the asset.
     * @return {this}
     */

  }, {
    key: "setDeliveryType",
    value: function setDeliveryType(newType) {
      this.deliveryType = newType;
      return this;
    }
    /**
     * @description Sets the URL SEO suffix of the asset.
     * @param {string} newSuffix The SEO suffix.
     * @return {this}
     */

  }, {
    key: "setSuffix",
    value: function setSuffix(newSuffix) {
      this.suffix = newSuffix;
      return this;
    }
    /**
     * @description Sets the signature of the asset.
     * @param {string} signature The signature.
     * @return {this}
     */

  }, {
    key: "setSignature",
    value: function setSignature(signature) {
      this.signature = signature;
      return this;
    }
    /**
     * @description Sets the version of the asset.
     * @param {string} newVersion The version of the asset.
     * @return {this}
     */

  }, {
    key: "setVersion",
    value: function setVersion(newVersion) {
      if (newVersion) {
        this.version = newVersion;
      }

      return this;
    }
    /**
     * @description Sets the asset type.
     * @param {string} newType The type of the asset.
     * @return {this}
     */

  }, {
    key: "setAssetType",
    value: function setAssetType(newType) {
      if (newType) {
        this.assetType = newType;
      }

      return this;
    }
  }, {
    key: "sign",
    value: function sign() {
      return this;
    }
    /**
     * @description Serializes to URL string
     * @param overwriteOptions
     */

  }, {
    key: "toURL",
    value: function toURL() {
      var overwriteOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return this.createCloudinaryURL(null, overwriteOptions.trackedAnalytics);
    }
    /**
     * @description Validate various options before attempting to create a URL
     * The function will throw in case a violation
     * @throws Validation errors
     */

  }, {
    key: "validateAssetForURLCreation",
    value: function validateAssetForURLCreation() {
      if (typeof this.cloudName === 'undefined') {
        throw 'You must supply a cloudName when initializing the asset';
      }

      var suffixContainsDot = this.suffix && this.suffix.indexOf('.') >= 0;
      var suffixContainsSlash = this.suffix && this.suffix.indexOf('/') >= 0;

      if (suffixContainsDot || suffixContainsSlash) {
        throw '`suffix`` should not include . or /';
      }
    }
    /**
     * @description return an SEO friendly name for a combination of asset/delivery, some examples:
     * * image/upload -> images
     * * video/upload -> videos
     * If no match is found, return `{asset}/{delivery}`
     */

  }, {
    key: "getResourceType",
    value: function getResourceType() {
      var assetType = handleAssetType(this.assetType);
      var deliveryType = handleDeliveryType(this.deliveryType);
      var hasSuffix = !!this.suffix;
      var regularSEOType = "".concat(assetType, "/").concat(deliveryType);
      var shortSEOType = SEO_TYPES["".concat(assetType, "/").concat(deliveryType)];
      var useRootPath = this.urlConfig.useRootPath;
      var shorten = this.urlConfig.shorten; // Quick exit incase of useRootPath

      if (useRootPath) {
        if (regularSEOType === 'image/upload') {
          return ''; // For image/upload we're done, just return nothing
        } else {
          throw new Error("useRootPath can only be used with assetType: 'image' and deliveryType: 'upload'. Provided: ".concat(regularSEOType, " instead"));
        }
      }

      if (shorten && regularSEOType === 'image/upload') {
        return 'iu';
      }

      if (hasSuffix) {
        if (shortSEOType) {
          return shortSEOType;
        } else {
          throw new Error("URL Suffix only supported for ".concat(Object.keys(SEO_TYPES).join(', '), ", Provided: ").concat(regularSEOType, " instead"));
        }
      } // If all else fails, return the regular image/upload combination (asset/delivery)


      return regularSEOType;
    }
  }, {
    key: "getSignature",
    value: function getSignature() {
      if (this.signature) {
        return "s--".concat(this.signature, "--");
      } else {
        return '';
      }
    }
    /**
     *
     * @description Creates a fully qualified CloudinaryURL
     * @return {string} CloudinaryURL
     * @throws Validation Errors
     */

  }, {
    key: "createCloudinaryURL",
    value: function createCloudinaryURL(transformation, trackedAnalytics) {
      // In accordance with the existing implementation, if no publicID exists we should return nothing.
      if (!this.publicID) {
        return '';
      } // Throws if some options are mis-configured
      // See the function for more information on when it throws


      this.validateAssetForURLCreation();
      var prefix = getUrlPrefix(this.cloudName, this.urlConfig);
      var transformationString = transformation ? transformation.toString() : '';
      var version = getUrlVersion(this.publicID, this.version, this.urlConfig.forceVersion);
      var publicID = this.publicID;

      if (typeof transformation === 'string') {
        var url = [prefix, this.getResourceType(), this.getSignature(), transformationString, version, publicID.replace(/,/g, '%2C'), this.suffix].filter(function (a) {
          return a;
        }).join('/');
        return url;
      } else {
        // Avoid applying encodeURI on entire string in case where we have transformations with comma (i.e. f_auto,q_auto)
        // Since encodeURI does not encode commas we replace commas *only* on the publicID
        var safeURL = [encodeURI(prefix), this.getResourceType(), this.getSignature(), encodeURI(transformationString), version, encodeURI(publicID).replace(/,/g, '%2C'), this.suffix && encodeURI(this.suffix)].filter(function (a) {
          return a;
        }).join('/').replace(/\?/g, '%3F').replace(/=/g, '%3D');
        var shouldAddAnalytics = this.urlConfig.analytics !== false && !publicID.includes('?');
        var queryParamsString = '';

        if (typeof this.urlConfig.queryParams === 'object') {
          try {
            var queryParams = new URLSearchParams(this.urlConfig.queryParams);

            if (shouldAddAnalytics) {
              queryParams.set("_a", getSDKAnalyticsSignature(trackedAnalytics));
            }

            queryParamsString = queryParams.toString();
          } catch (err) {
            console.error('Error: URLSearchParams is not available so the queryParams object cannot be parsed, please try passing as an already parsed string');
          }
        } else {
          queryParamsString = this.urlConfig.queryParams || '';

          if (shouldAddAnalytics) {
            queryParamsString += "".concat(queryParamsString.length > 0 ? '&' : '', "_a=").concat(getSDKAnalyticsSignature(trackedAnalytics));
          }
        }

        if (queryParamsString) {
          return "".concat(safeURL, "?").concat(queryParamsString);
        } else {
          return safeURL;
        }
      }
    }
  }]);

  return CloudinaryFile;
}();

export { CloudinaryFile };